import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Building2,
  Users,
  BarChart3,
  CreditCard,
  Settings,
  Bug,
  ArrowLeft,
  LayoutDashboard,
  Store,
  Activity,
  Ticket,
  Inbox,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { solicitudesService } from '@/features/solicitudes/services/solicitudes.service';

/*
  La barra del panel de plataforma.

  Vive en su propio archivo y no como una rama dentro de `sidebar.tsx` por dos
  razones: `sidebar.tsx` ya pasa las 1600 lineas y su trabajo es otro —armar el
  menu del portal filtrando por los permisos de la persona—, y esta barra no
  filtra nada: quien llega aca ya paso por `RequireRole`.

  El acento `plataforma` es el que ya existe en el sistema de diseño para marcar
  que estas actuando sobre OTRA empresa. Aca marca el plano entero.
*/
const SECCIONES = [
  { a: '/plataforma', rotulo: 'Panel', icono: LayoutDashboard, exacto: true },
  { a: '/plataforma/empresas', rotulo: 'Empresas', icono: Building2, exacto: false },
  { a: '/plataforma/personas', rotulo: 'Personas', icono: Users, exacto: false },
  // Landing nueva (vault 346): lo que llega de las páginas públicas, con el distintivo de las nuevas.
  { a: '/plataforma/solicitudes', rotulo: 'Solicitudes', icono: Inbox, exacto: false },
  { a: '/plataforma/invitaciones', rotulo: 'Invitaciones', icono: Ticket, exacto: false },
  /*
    Plan 297 (S2): el catálogo de tiendas de Prixia es de la plataforma (C3). Qué
    tiendas ve cada empresa se decide en su ficha («Tiendas disponibles»).
  */
  { a: '/plataforma/tiendas', rotulo: 'Tiendas de Prixia', icono: Store, exacto: false },
  { a: '/plataforma/lecturas', rotulo: 'Lecturas', icono: Activity, exacto: false },
  /*
    «Permisos y módulos» NO va acá: son siempre de UNA empresa, y se llega desde
    su ficha. Una entrada suelta en la barra prometería una pantalla global que
    no existe. La maqueta la dibujaba en la barra y estaba mal.
  */
  { a: '/plataforma/comparacion', rotulo: 'Comparación', icono: BarChart3, exacto: false },
  { a: '/plataforma/planes', rotulo: 'Planes', icono: CreditCard, exacto: false },
  { a: '/plataforma/configuracion', rotulo: 'Configuración', icono: Settings, exacto: false },
  { a: '/plataforma/bugs', rotulo: 'Reportes de bugs', icono: Bug, exacto: false },
] as const;

/** Cuántas solicitudes esperan revisión. Se vuelve a contar al cambiar de pantalla; si falla, 0. */
function useSolicitudesNuevas(): number {
  const { pathname } = useLocation();
  const [nuevas, setNuevas] = useState(0);
  useEffect(() => {
    let vigente = true;
    solicitudesService
      .listar({ estado: 'NUEVA' })
      .then((l) => vigente && setNuevas(l.nuevas))
      .catch(() => vigente && setNuevas(0));
    return () => {
      vigente = false;
    };
  }, [pathname]);
  return nuevas;
}

export function BarraPlataforma() {
  const nuevas = useSolicitudesNuevas();
  return (
    <nav className="banda-plataforma flex h-full w-56 flex-col border-r border-border bg-muted/40 py-4">
      <div className="px-4 pb-3 text-[10px] font-bold uppercase tracking-[0.1em] text-plataforma">
        Plataforma · DATEC
      </div>

      <div className="flex flex-1 flex-col gap-0.5">
        {SECCIONES.map(({ a, rotulo, icono: Icono, exacto }) => (
          <NavLink
            key={a}
            to={a}
            end={exacto}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-4 py-1.5 text-sm text-foreground transition-colors',
                isActive ? 'bg-plataforma-suave font-semibold text-plataforma' : 'hover:bg-accent'
              )
            }
          >
            <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
            {rotulo}
            {a === '/plataforma/solicitudes' && nuevas > 0 && (
              <span
                aria-label={`, ${nuevas} ${nuevas === 1 ? 'nueva' : 'nuevas'}`}
                className="ml-auto rounded-full bg-prixia-gold px-1.5 text-[10px] font-bold leading-4 text-white" // pulido: permitido — contador sólido como el de la campana
              >
                {nuevas}
              </span>
            )}
          </NavLink>
        ))}
      </div>

      <NavLink
        to="/portales"
        className="mt-4 flex items-center gap-2 border-t border-border px-4 pt-4 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Volver a los portales
      </NavLink>
    </nav>
  );
}

export default BarraPlataforma;
